import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useSlaveEvaluations from '@/data/hook/evaluation/useSlaveEvaluations';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { evaluationQueries } from '@/data/query-keys/evaluationQueryKey';
import {
  createEvaluation,
  deleteEvaluation,
  updateEvaluation,
} from '@/data/services/evaluationServices';
import Evaluation, { EvaluationTypeEnum } from '@/models/Evaluation';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { keyBy } from 'lodash';
import { useInfiniteCache } from '../query/v2/useCacheManager';

type UseEvaluationsProps = {
  courseLineId: number;
  pageSize?: number;
};

type CreateMutationArgs = {
  name: string;
  coursePathIds: number[];
};

type UpdateMutationArgs = {
  evaluationId: number;
  data: Partial<Evaluation>;
  coursePathIds: number[];
};

const useMasterEvaluations = ({
  courseLineId,
  pageSize,
}: UseEvaluationsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const {
    results: evaluations,
    isInitialLoading: isMasterLoading,
    cacheManager,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteService<Evaluation>({
    ...evaluationQueries.listInfinite({
      courseLineId,
      type: EvaluationTypeEnum.TEMPLATE,
      pageSize,
    }),
    enabled: !!courseLineId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const infinityCacheManager = useInfiniteCache(
    evaluationQueries.listInfinite({
      courseLineId,
      type: EvaluationTypeEnum.TEMPLATE,
    }).queryKey,
  );

  const listCacheManager = useInfiniteCache(
    evaluationQueries.listInfinite({
      courseLineId,
      type: EvaluationTypeEnum.TEMPLATE,
      pageSize: ORDENABLE_PAGE_SIZE,
    }).queryKey,
  );

  const {
    syncCoursePaths,
    slavesByMaster,
    isLoading: isSlaveLoading,
  } = useSlaveEvaluations({
    courseLineId,
    masters: evaluations,
  });

  const evaluationsMap = keyBy(evaluations, 'id');

  const handleCreate = async ({ name, coursePathIds }: CreateMutationArgs) => {
    const evaluation = await createEvaluation({
      name,
      type: EvaluationTypeEnum.TEMPLATE,
      courseLineId,
      isDefault: false,
    });
    await syncCoursePaths(evaluation.id, coursePathIds);
    return evaluation;
  };

  const handleUpdate = async ({
    evaluationId,
    data,
    coursePathIds,
  }: UpdateMutationArgs) => {
    const evaluation = await updateEvaluation(evaluationId, data);
    await syncCoursePaths(evaluationId, coursePathIds);
    return evaluation;
  };

  const createMutation = useMutation({
    mutationFn: handleCreate,
    onSuccess: evaluation => {
      infinityCacheManager.add(evaluation);
      listCacheManager.add(evaluation);
      alert.success(t('createBlockSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (evaluationId: number) => deleteEvaluation(evaluationId),
    onSuccess: (_, evaluationId) => {
      infinityCacheManager.remove(evaluationId);
      listCacheManager.remove(evaluationId);
      alert.success(t('deleteBlockSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: handleUpdate,
    onSuccess: evaluation => {
      infinityCacheManager.update(evaluation);
      listCacheManager.update(evaluation);
      alert.success(t('updateBlockSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const isLoading = isMasterLoading || isSlaveLoading;

  return {
    evaluations,
    evaluationsMap,
    slavesByMaster,
    isLoading,
    cacheManager,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    createEvaluation: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    deleteEvaluation: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    updateEvaluation: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
};

export default useMasterEvaluations;
